import { useMemo, useState } from "react";

import { Stack } from "@mui/material";
import {
  FormType,
  formatCurrencyFromCents,
  formatSweepstakesEntryLevels,
  getIsoLocaleFromCountry,
  getSweepstakesCountryName,
  getSweepstakesEligibleTerritory,
  getSweepstakesFreeEntryCount,
  getSweepstakesSponsorEntityType,
  getSweepstakesSponsorTaxIdLabel,
  getTicketingLink,
  SweepstakesRulesInput,
  PaymentMethod,
  parseFormBackgroundTheme,
} from "@simplyk/common";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { useLocaleField } from "@/canvas/features/FormV2/helpers/useLocaleField";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { getSweepstakesRulesDates } from "@/common/helpers/sweepstakes";
import { getThankYouPageUrl, navigateAfterPayment } from "@/common/helpers/thankYouPage";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { FormDivider } from "@/forms-design-system/FormDivider";

import { SweepstakesFreeEntryDialog } from "./SweepstakesFreeEntryDialog";
import { SweepstakesRules } from "./SweepstakesRules";

export const SweepstakesSection = () => {
  const [freeEntryOpen, setFreeEntryOpen] = useState(false);
  const {
    form,
    formObject: ticketing,
    formCurrency,
    formOrganization,
    validRatesSorted,
    isPreview,
  } = useFormV2Context(FormType.Ticketing);
  const { isoLocale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const getLocaleField = useLocaleField();
  const ticketingOccurrenceId = form.watch("occurrenceId");

  const rulesInput = useMemo<SweepstakesRulesInput>(() => {
    const organizationAddress = [
      formOrganization.address,
      formOrganization.city,
      formOrganization.region,
      formOrganization.postalCode,
      getSweepstakesCountryName(formOrganization.country),
    ]
      .filter(Boolean)
      .join(", ");
    const entryLevelsTable = formatSweepstakesEntryLevels(
      validRatesSorted.map((rate) => ({
        title: getLocaleField(rate.rateFields)?.title,
        entryCount: rate.attendeesByTicket,
        formattedAmount: formatCurrencyFromCents(rate.amount ?? 0, { isoLocale, currency: formCurrency }),
      }))
    );
    const rulesTimezone = ticketing.eventTimezone || formOrganization.timezone || "UTC";
    const freeEntryEligibleRates = (ticketing.rates ?? []).filter((rate) => !("isArchived" in rate && rate.isArchived));
    const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL;
    const campaignUrl =
      frontendUrl &&
      `${frontendUrl.replace(/\/$/, "")}${getTicketingLink(
        ticketing.path,
        getIsoLocaleFromCountry(formOrganization.country, ticketing.locale)
      )}`;

    return {
      country: formOrganization.country,
      sponsorLegalName: formOrganization.legalName || formOrganization.name,
      sponsorEntityType: getSweepstakesSponsorEntityType(formOrganization.country, formOrganization.type),
      sponsorTaxIdLabel: getSweepstakesSponsorTaxIdLabel(formOrganization.country, formOrganization.type),
      sponsorTaxId: ticketing.sweepstakesSponsor?.taxId,
      sponsorFullAddress: organizationAddress,
      sponsorContactPhone: ticketing.sweepstakesSponsor?.phoneNumber,
      eligibleTerritory: getSweepstakesEligibleTerritory(formOrganization.country),
      ...getSweepstakesRulesDates(ticketing, rulesTimezone),
      campaignUrl,
      entryLevelsTable,
      sponsorContactEmail: formOrganization.ownerEmail,
      freeEntriesPerRequest:
        (isPreview ? undefined : ticketing.sweepstakesFreeEntryCount) ??
        getSweepstakesFreeEntryCount(freeEntryEligibleRates),
      sweepstakesName: getLocaleField(ticketing.ticketingFields)?.title || null,
      zeffyPrivacyPolicyUrl: "https://www.zeffy.com/en-US/privacy-policy",
    };
  }, [formCurrency, formOrganization, getLocaleField, isoLocale, isPreview, ticketing, validRatesSorted]);

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <FormDivider sx={{ width: "100%" }} />
      <SweepstakesRules
        input={rulesInput}
        onOpenFreeEntry={() => {
          logAmplitudeEvent(AmplitudeEvents.SweepstakesFreeEntryOpened, {
            ticketingId: ticketing.id,
            is_sweepstakes: true,
          });
          setFreeEntryOpen(true);
        }}
      />
      <SweepstakesFreeEntryDialog
        open={freeEntryOpen}
        onClose={() => setFreeEntryOpen(false)}
        ticketingId={ticketing.id}
        ticketingOccurrenceId={ticketingOccurrenceId}
        onConfirmed={async ({ email }) => {
          await navigateAfterPayment(
            getThankYouPageUrl({
              command: {
                email,
                totalAmount: 0,
                tipAmount: 0,
                productsAmount: 0,
                eligibleAmount: 0,
                paymentMethod: PaymentMethod.Free,
              },
              formType: FormType.Ticketing,
              organization: { ...formOrganization, canGenerateReceipt: false },
              postTransactionUrl: null,
              isEmbeddedModal: false,
              generateETicket: false,
              formData: ticketing,
              isoLocale,
              category: ticketing.formCategory,
              isAuction: false,
              themeColor: ticketing.color,
              isDarkMode: Boolean(ticketing.isDarkMode),
              backgroundTheme: parseFormBackgroundTheme(ticketing.backgroundTheme),
              isEmbed: false,
              isSweepstakesFreeEntry: true,
            })
          );
        }}
      />
    </Stack>
  );
};
